import { useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, Icon, useToast } from '@kollez/design-system';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import { useAuthStore } from '@/lib/authStore';
import { useSkillTaxonomy, useStudentSkillsGap } from '@/lib/queries';

interface Skill {
  _id?: string;
  name: string;
  slug?: string;
  category?: string;
  proficiencyLevel?: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  yearsOfExperience?: number;
  verified?: boolean;
}
type Level = 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';

/**
 * /skills — Student's self-declared + assessment-verified skills.
 *
 * Deep-audit fixes (Page 8/N):
 *   - Free-text 'name' input silently produced React / React.js / reactjs
 *     variants that never match Opportunity.requiredSkills. Now a datalist
 *     backed by /skill-taxonomy so students pick from the canonical 73-slug
 *     vocabulary (they can still type-and-add, but the autocomplete nudges
 *     them to canonical names).
 *   - No skills-gap surface — /students/:id/skills-gap (Pass 11) is the exact
 *     endpoint that answers 'which skills would open more drives?'. Now
 *     rendered as a live section with per-skill demand counts + one-tap Add.
 *   - No verification hint — 'verified' badge now shows a tooltip explaining
 *     it comes from Assessment attempts.
 *   - Registry-safe icons only.
 */
export function StudentSkillsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const [name, setName] = useState('');
  const [level, setLevel] = useState<Level>('Intermediate');

  const taxonomy = useSkillTaxonomy();
  const skillsGap = useStudentSkillsGap(user?.id);

  const query = useQuery({
    queryKey: ['student', 'me', 'skills'],
    queryFn: () => apiGet<{ rows: Skill[] }>('/students/me/skills'),
  });

  const add = useMutation({
    mutationFn: (input: { name: string; proficiencyLevel: string }) => apiPost('/students/me/skills', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['student', 'me', 'skills'] });
      qc.invalidateQueries({ queryKey: ['student-skills-gap'] });
      setName('');
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/students/me/skills/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['student', 'me', 'skills'] });
      qc.invalidateQueries({ queryKey: ['student-skills-gap'] });
    },
  });

  const skills = query.data?.rows ?? [];
  const verifiedCount = useMemo(() => skills.filter((s) => s.verified).length, [skills]);
  const ownedSlugs = useMemo(
    () => new Set(skills.map((s) => (s.slug ?? s.name).trim().toLowerCase())),
    [skills],
  );

  function addFromGap(displayName: string) {
    add.mutate(
      { name: displayName, proficiencyLevel: 'Beginner' },
      { onSuccess: () => toast.show({ message: `Added "${displayName}".` }) },
    );
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">My profile</p>
          <h1 className="h-page">Skills</h1>
          <p className="subhead">
            Tell recruiters what you can do. A skill that clears an assessment gets a verified badge.
            {skills.length > 0 && ` · ${skills.length} listed · ${verifiedCount} verified`}
          </p>
        </div>
      </div>

      <section className="card card-pad grid gap-3">
        <h2 className="h-section">Add a skill</h2>
        <div className="flex gap-2 flex-wrap">
          <input
            className="input"
            list="skill-taxonomy-datalist"
            placeholder="e.g. React, SQL, Verilog"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ minWidth: 220 }}
          />
          {/* HTML5 datalist backed by the canonical /skill-taxonomy vocabulary.
              Native browsers filter suggestions client-side; large lists (73
              entries today) render instantly. */}
          <datalist id="skill-taxonomy-datalist">
            {(taxonomy.data?.rows ?? []).map((s) => (
              <option key={s.slug} value={s.displayName}>{s.taxonomy}</option>
            ))}
          </datalist>
          <select className="select" value={level} onChange={(e) => setLevel(e.target.value as Level)}>
            {(['Beginner', 'Intermediate', 'Advanced', 'Expert'] as const).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <button
            className="btn btn-primary"
            disabled={add.isPending || !name.trim()}
            onClick={() => add.mutate(
              { name: name.trim(), proficiencyLevel: level },
              {
                onSuccess: () => toast.show({ message: `Added "${name}".` }),
                onError: (e) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Failed' }),
              },
            )}
          >
            Add
          </button>
        </div>
        <p className="helper m-0">
          Start typing to pick from Kollez's canonical vocabulary. Recruiters filter drives by these
          exact names — using a canonical slug means more matches.
        </p>
      </section>

      <AsyncBoundary
        kind="cards"
        loading={query.isLoading}
        error={query.error}
        empty={query.data?.rows.length === 0}
        emptyTitle="No skills recorded yet"
      >
        <section className="card">
          <div className="card-head">
            <h2 className="h-section">Your skills</h2>
            <span className="helper">{verifiedCount}/{skills.length} verified</span>
          </div>
          <div className="card-pad">
            <div className="flex flex-wrap gap-2">
              {skills.map((s) => (
                <span
                  key={s._id ?? s.name}
                  className="chip"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 999,
                    background: 'rgb(var(--bg-secondary))',
                    border: '1px solid rgb(var(--line-tertiary))',
                  }}
                >
                  {s.verified && (
                    <span
                      className="badge badge-success"
                      title="Verified via an Assessment attempt"
                      aria-label="Verified"
                    >
                      ✓
                    </span>
                  )}
                  {s.name}
                  {s.proficiencyLevel && <span className="cell-sub">· {s.proficiencyLevel}</span>}
                  {s._id && (
                    <button
                      className="btn btn-ghost btn-icon btn-sm"
                      aria-label={`Remove ${s.name}`}
                      onClick={() => del.mutate(s._id!, {
                        onSuccess: () => toast.show({ message: `Removed "${s.name}".` }),
                      })}
                    >
                      <Icon name="trash-2" size={12} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        </section>
      </AsyncBoundary>

      {/* Skills gap — Pass 11 endpoint. Only rendered when the gap query has data. */}
      {skillsGap.data && (skillsGap.data.missingRequired.length > 0 || skillsGap.data.missingPreferred.length > 0) && (
        <section className="card">
          <div className="card-head">
            <h2 className="h-section">Skills that would open more drives</h2>
            <span className="helper">{skillsGap.data.opportunitiesConsidered} live opportunit{skillsGap.data.opportunitiesConsidered === 1 ? 'y' : 'ies'} considered</span>
          </div>
          <div className="card-pad grid gap-4">
            {skillsGap.data.missingRequired.length > 0 && (
              <div>
                <div className="text-sm font-semibold mb-2">Required by opportunities you'd otherwise fit</div>
                <div className="flex flex-wrap gap-2">
                  {skillsGap.data.missingRequired.slice(0, 12).map((g) => {
                    const already = ownedSlugs.has(g.skill.trim().toLowerCase());
                    return (
                      <button
                        key={g.skill}
                        className="chip"
                        disabled={already || add.isPending}
                        onClick={() => addFromGap(g.skill)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '4px 10px', borderRadius: 999,
                          background: already ? 'rgb(var(--bg-tertiary))' : 'rgb(var(--bg-secondary))',
                          border: '1px solid rgb(var(--line-tertiary))',
                          cursor: already ? 'default' : 'pointer',
                        }}
                        aria-label={`Add ${g.skill}`}
                        title={`Required in ${g.requiredIn} opportunit${g.requiredIn === 1 ? 'y' : 'ies'}`}
                      >
                        <span>{g.skill}</span>
                        <span className="cell-sub">× {g.requiredIn}</span>
                        {!already && <span aria-hidden>＋</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {skillsGap.data.missingPreferred.length > 0 && (
              <div>
                <div className="text-sm font-semibold mb-2">Preferred (nice-to-have)</div>
                <div className="flex flex-wrap gap-2">
                  {skillsGap.data.missingPreferred.slice(0, 12).map((g) => {
                    const already = ownedSlugs.has(g.skill.trim().toLowerCase());
                    return (
                      <button
                        key={g.skill}
                        className="chip"
                        disabled={already || add.isPending}
                        onClick={() => addFromGap(g.skill)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '4px 10px', borderRadius: 999,
                          background: already ? 'rgb(var(--bg-tertiary))' : 'rgb(var(--bg-secondary))',
                          border: '1px solid rgb(var(--line-tertiary))',
                          cursor: already ? 'default' : 'pointer',
                        }}
                        aria-label={`Add ${g.skill}`}
                        title={`Preferred in ${g.preferredIn} opportunit${g.preferredIn === 1 ? 'y' : 'ies'}`}
                      >
                        <span>{g.skill}</span>
                        <span className="cell-sub">× {g.preferredIn}</span>
                        {!already && <span aria-hidden>＋</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <CertificationsSection />
    </div>
  );
}

interface CertificationRow {
  _id?: string;
  name?: string;
  issuer?: string;
  issuedOn?: string;
  expiresOn?: string;
  credentialUrl?: string;
  skill?: string;
}

/**
 * Ship 5b seeded SkillCertification rows under StudentProfile.certifications.
 * The /me/certifications endpoint returns them; no page rendered them until now.
 * Read-only surface — issuers are third parties (Coursera, NPTEL, etc.), students
 * can't self-add here; those flow in via /me/documents verification instead.
 */
function CertificationsSection() {
  const q = useQuery({
    queryKey: ['student', 'me', 'certifications'],
    queryFn: () => apiGet<{ rows: CertificationRow[] }>('/students/me/certifications'),
  });
  const rows = q.data?.rows ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="h-section">Certifications</h2>
        <span className="cell-sub">{rows.length}</span>
      </div>
      <AsyncBoundary kind="table" inCard loading={q.isLoading} error={q.error} empty={rows.length === 0} emptyTitle="No certifications on file yet" emptyHint="Add a credential URL via Documents; it appears here once verified.">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Issuer</th>
                <th className="hide-mobile">Issued</th>
                <th className="hide-mobile">Skill</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c._id ?? i}>
                  <td className="cell-title">{c.name ?? '—'}</td>
                  <td>{c.issuer ?? '—'}</td>
                  <td className="hide-mobile cell-sub">
                    {c.issuedOn ? new Date(c.issuedOn).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="hide-mobile cell-sub">{c.skill ?? '—'}</td>
                  <td className="r">
                    {c.credentialUrl ? (
                      <a className="btn btn-ghost btn-sm" href={c.credentialUrl} target="_blank" rel="noopener noreferrer">
                        <Icon name="external-link" size={12} />Open
                      </a>
                    ) : <span className="cell-sub">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>
    </section>
  );
}
